// Supported caption/translation language lists -- real, verified data
// fetched directly from each vendor's own docs/API this session, not
// guessed or approximated. Used by Captions.js to constrain the language
// pickers to a dropdown of what each service actually accepts, instead of
// a free-text field an operator could put an invalid code into.
//
// CC1_LANGUAGES_BY_PROVIDER: real-time/streaming recognition languages
// specifically (not each vendor's full/batch-only language list, which is
// often larger but partly unusable here) --
//   - Deepgram Nova-3: developers.deepgram.com/docs/models-languages-overview
//   - Google Cloud Speech-to-Text Chirp 3, StreamingRecognize, GA status
//     only (not the larger preview-status list, which trades stability
//     for coverage): docs.cloud.google.com/speech-to-text/docs/models/chirp-3
//   - Speechmatics real-time: docs.speechmatics.com/speech-to-text/languages
//     (excludes its bilingual/composite codes like "ar_en" or
//     "cmn_en_ms_ta" -- those are a different, dual-language recognition
//     mode, not a simple single-language selection this picker models).
//     en-GB/en-US/en-AU alongside plain "en" here are NOT separate
//     recognition languages the way they are for Deepgram/Google --
//     Speechmatics uses a single accent-agnostic pack that already covers
//     every English accent for recognition itself (confirmed against
//     Speechmatics' own docs). They exist in this list purely so the
//     operator can pick a spelling convention ("colour" vs "color") from
//     the same dropdown they already use for language -- restreamer_
//     captions.py's _stt_process_config derives Speechmatics'
//     output_locale straight from cc1_lang when it's one of these three
//     codes (see that function's speechmatics branch); the recognition
//     --language sent to speechmatics_client.py still normalizes to bare
//     "en" either way (see build_start_recognition_message's docstring on
//     why a region-qualified code can't be sent as-is).
//

// CC3_LANGUAGES: Google Cloud Translation API v2's actual supported target
// languages, fetched live against the real API this session (GET
// https://translation.googleapis.com/language/translate/v2/languages) --
// the definitive list, not a copy of published docs that can drift.
// Independent of transcription_provider -- translation always goes
// through the same Translator regardless of which STT vendor produced
// the source text (see caption_injector.py's Translator class).
//
// A handful of legacy/alternate ISO codes the raw API response included
// (e.g. "iw" alongside "he" for Hebrew, "jw" alongside "jv" for Javanese,
// "zh-CN" alongside "zh" for Chinese Simplified) are deliberately omitted
// here -- they're the same language as their listed counterpart, not a
// real distinction this audience needs to make, and including both as
// unexplained near-duplicate entries is exactly the kind of internal-
// implementation-detail leak PRODUCT.md's design principles call out.

export const CC1_LANGUAGES_BY_PROVIDER = {
	deepgram: [
		{ code: 'multi', label: 'Multilingual (auto-detect)' },
		{ code: 'ar', label: 'Arabic' },
		{ code: 'ar-AE', label: 'Arabic (UAE)' },
		{ code: 'ar-SA', label: 'Arabic (Saudi Arabia)' },
		{ code: 'ar-QA', label: 'Arabic (Qatar)' },
		{ code: 'ar-KW', label: 'Arabic (Kuwait)' },
		{ code: 'ar-SY', label: 'Arabic (Syria)' },
		{ code: 'ar-LB', label: 'Arabic (Lebanon)' },
		{ code: 'ar-PS', label: 'Arabic (Palestine)' },
		{ code: 'ar-JO', label: 'Arabic (Jordan)' },
		{ code: 'ar-EG', label: 'Arabic (Egypt)' },
		{ code: 'ar-SD', label: 'Arabic (Sudan)' },
		{ code: 'ar-TD', label: 'Arabic (Chad)' },
		{ code: 'ar-MA', label: 'Arabic (Morocco)' },
		{ code: 'ar-DZ', label: 'Arabic (Algeria)' },
		{ code: 'ar-TN', label: 'Arabic (Tunisia)' },
		{ code: 'ar-IQ', label: 'Arabic (Iraq)' },
		{ code: 'ar-IR', label: 'Arabic (Iran)' },
		{ code: 'be', label: 'Belarusian' },
		{ code: 'bn', label: 'Bengali' },
		{ code: 'bs', label: 'Bosnian' },
		{ code: 'bg', label: 'Bulgarian' },
		{ code: 'ca', label: 'Catalan' },
		{ code: 'zh-HK', label: 'Chinese (Cantonese, Traditional)' },
		{ code: 'zh', label: 'Chinese (Mandarin, Simplified)' },
		{ code: 'zh-CN', label: 'Chinese (Mandarin, Simplified, China)' },
		{ code: 'zh-Hans', label: 'Chinese (Mandarin, Simplified, Hans)' },
		{ code: 'zh-TW', label: 'Chinese (Mandarin, Traditional, Taiwan)' },
		{ code: 'zh-Hant', label: 'Chinese (Mandarin, Traditional, Hant)' },
		{ code: 'hr', label: 'Croatian' },
		{ code: 'cs', label: 'Czech' },
		{ code: 'da', label: 'Danish' },
		{ code: 'da-DK', label: 'Danish (Denmark)' },
		{ code: 'nl', label: 'Dutch' },
		{ code: 'nl-BE', label: 'Flemish (Belgian Dutch)' },
		{ code: 'en', label: 'English' },
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-IN', label: 'English (India)' },
		{ code: 'en-NZ', label: 'English (New Zealand)' },
		{ code: 'et', label: 'Estonian' },
		{ code: 'fi', label: 'Finnish' },
		{ code: 'fr', label: 'French' },
		{ code: 'fr-CA', label: 'French (Canada)' },
		{ code: 'de', label: 'German' },
		{ code: 'de-CH', label: 'German (Switzerland)' },
		{ code: 'el', label: 'Greek' },
		{ code: 'gu', label: 'Gujarati' },
		{ code: 'gu-IN', label: 'Gujarati (India)' },
		{ code: 'he', label: 'Hebrew' },
		{ code: 'hi', label: 'Hindi' },
		{ code: 'hu', label: 'Hungarian' },
		{ code: 'id', label: 'Indonesian' },
		{ code: 'it', label: 'Italian' },
		{ code: 'ja', label: 'Japanese' },
		{ code: 'kn', label: 'Kannada' },
		{ code: 'ko', label: 'Korean' },
		{ code: 'ko-KR', label: 'Korean (South Korea)' },
		{ code: 'lv', label: 'Latvian' },
		{ code: 'lt', label: 'Lithuanian' },
		{ code: 'mk', label: 'Macedonian' },
		{ code: 'ms', label: 'Malay' },
		{ code: 'mr', label: 'Marathi' },
		{ code: 'no', label: 'Norwegian' },
		{ code: 'fa', label: 'Persian' },
		{ code: 'pl', label: 'Polish' },
		{ code: 'pt', label: 'Portuguese' },
		{ code: 'pt-BR', label: 'Portuguese (Brazil)' },
		{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
		{ code: 'ro', label: 'Romanian' },
		{ code: 'ru', label: 'Russian' },
		{ code: 'sr', label: 'Serbian' },
		{ code: 'sk', label: 'Slovak' },
		{ code: 'sl', label: 'Slovenian' },
		{ code: 'es', label: 'Spanish' },
		{ code: 'es-419', label: 'Spanish (Latin America)' },
		{ code: 'sv', label: 'Swedish' },
		{ code: 'sv-SE', label: 'Swedish (Sweden)' },
		{ code: 'tl', label: 'Tagalog' },
		{ code: 'ta', label: 'Tamil' },
		{ code: 'te', label: 'Telugu' },
		{ code: 'th', label: 'Thai' },
		{ code: 'th-TH', label: 'Thai (Thailand)' },
		{ code: 'tr', label: 'Turkish' },
		{ code: 'uk', label: 'Ukrainian' },
		{ code: 'ur', label: 'Urdu' },
		{ code: 'vi', label: 'Vietnamese' },
	],

	// Chirp 3, StreamingRecognize, GA (Generally Available) status only.
	// Chirp 3 also supports 70+ additional "preview" status languages for
	// streaming, deliberately left out here -- preview languages don't
	// carry the same stability/quality guarantee as GA, not a great match
	// for a default operator-facing picker.
	google: [
		{ code: 'ca-ES', label: 'Catalan (Spain)' },
		{ code: 'cmn-Hans-CN', label: 'Chinese, Mandarin (Simplified, China)' },
		{ code: 'hr-HR', label: 'Croatian (Croatia)' },
		{ code: 'da-DK', label: 'Danish (Denmark)' },
		{ code: 'nl-NL', label: 'Dutch (Netherlands)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en-IN', label: 'English (India)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'fi-FI', label: 'Finnish (Finland)' },
		{ code: 'fr-CA', label: 'French (Canada)' },
		{ code: 'fr-FR', label: 'French (France)' },
		{ code: 'de-DE', label: 'German (Germany)' },
		{ code: 'el-GR', label: 'Greek (Greece)' },
		{ code: 'hi-IN', label: 'Hindi (India)' },
		{ code: 'it-IT', label: 'Italian (Italy)' },
		{ code: 'ja-JP', label: 'Japanese (Japan)' },
		{ code: 'ko-KR', label: 'Korean (Korea)' },
		{ code: 'pl-PL', label: 'Polish (Poland)' },
		{ code: 'pt-BR', label: 'Portuguese (Brazil)' },
		{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
		{ code: 'ro-RO', label: 'Romanian (Romania)' },
		{ code: 'ru-RU', label: 'Russian (Russia)' },
		{ code: 'es-ES', label: 'Spanish (Spain)' },
		{ code: 'es-US', label: 'Spanish (US)' },
		{ code: 'sv-SE', label: 'Swedish (Sweden)' },
		{ code: 'tr-TR', label: 'Turkish (Turkey)' },
		{ code: 'uk-UA', label: 'Ukrainian (Ukraine)' },
		{ code: 'vi-VN', label: 'Vietnamese (Vietnam)' },
	],

	// Gemini 3.5 Transcribe Live (GEMINI_TRANSCRIBE_PLAN.md) -- a separate
	// provider from "google" above, not a replacement for it. The model
	// page claims 85+ languages for the Live surface, but that full list
	// was not fetched and verified code-by-code the way the four lists
	// above were, and this file's whole point is that it carries real
	// verified data rather than a plausible-looking guess -- so this starts
	// as the same Chirp 3 GA set (a conservative subset: these are the codes
	// this project already sends Google and gets captions back for), plus
	// the one genuinely new capability Chirp 3 never had. Widen it once the
	// model page's own language table has actually been fetched.
	//
	// No 'auto' entry here, deliberately, even though this provider really
	// does support auto-detection: cc1_lang is not only the recognition
	// language, it is also what caption_injector.py matches every incoming
	// fragment's lang against to decide which caption channel it lands on
	// (see its channel_for_lang). A cc1_lang of 'auto' would therefore
	// match nothing the model ever detects and silently drop every caption.
	// Auto-detection is exposed as its own gemini_auto_language toggle in
	// the Captions panel instead, which leaves cc1_lang a real language for
	// the injector to key on.
	gemini: [
		{ code: 'ca-ES', label: 'Catalan (Spain)' },
		{ code: 'cmn-Hans-CN', label: 'Chinese, Mandarin (Simplified, China)' },
		{ code: 'hr-HR', label: 'Croatian (Croatia)' },
		{ code: 'da-DK', label: 'Danish (Denmark)' },
		{ code: 'nl-NL', label: 'Dutch (Netherlands)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en-IN', label: 'English (India)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'fi-FI', label: 'Finnish (Finland)' },
		{ code: 'fr-CA', label: 'French (Canada)' },
		{ code: 'fr-FR', label: 'French (France)' },
		{ code: 'de-DE', label: 'German (Germany)' },
		{ code: 'el-GR', label: 'Greek (Greece)' },
		{ code: 'hi-IN', label: 'Hindi (India)' },
		{ code: 'it-IT', label: 'Italian (Italy)' },
		{ code: 'ja-JP', label: 'Japanese (Japan)' },
		{ code: 'ko-KR', label: 'Korean (Korea)' },
		{ code: 'pl-PL', label: 'Polish (Poland)' },
		{ code: 'pt-BR', label: 'Portuguese (Brazil)' },
		{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
		{ code: 'ro-RO', label: 'Romanian (Romania)' },
		{ code: 'ru-RU', label: 'Russian (Russia)' },
		{ code: 'es-ES', label: 'Spanish (Spain)' },
		{ code: 'es-US', label: 'Spanish (US)' },
		{ code: 'sv-SE', label: 'Swedish (Sweden)' },
		{ code: 'tr-TR', label: 'Turkish (Turkey)' },
		{ code: 'uk-UA', label: 'Ukrainian (Ukraine)' },
		{ code: 'vi-VN', label: 'Vietnamese (Vietnam)' },
	],

	// google_batch_diarized: same Chirp 3 model as the "google" (streaming)
	// entry above, just called through sync Recognize instead of
	// StreamingRecognize for real per-word diarization (see
	// DIARIZATION_PLAN.md and google_batch_client.py's own module
	// docstring -- Chirp 3 has no diarization data available in streaming
	// mode at all). Reuses that same GA language list on the assumption
	// that Recognize's GA language coverage matches StreamingRecognize's
	// -- not independently re-verified against Google's Recognize-specific
	// docs this session, flagged here rather than presented as confirmed.
	parakeet_local: [
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en-IN', label: 'English (India)' },
	],
	google_batch_diarized: [
		{ code: 'ca-ES', label: 'Catalan (Spain)' },
		{ code: 'cmn-Hans-CN', label: 'Chinese, Mandarin (Simplified, China)' },
		{ code: 'hr-HR', label: 'Croatian (Croatia)' },
		{ code: 'da-DK', label: 'Danish (Denmark)' },
		{ code: 'nl-NL', label: 'Dutch (Netherlands)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en-IN', label: 'English (India)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'fi-FI', label: 'Finnish (Finland)' },
		{ code: 'fr-CA', label: 'French (Canada)' },
		{ code: 'fr-FR', label: 'French (France)' },
		{ code: 'de-DE', label: 'German (Germany)' },
		{ code: 'el-GR', label: 'Greek (Greece)' },
		{ code: 'hi-IN', label: 'Hindi (India)' },
		{ code: 'it-IT', label: 'Italian (Italy)' },
		{ code: 'ja-JP', label: 'Japanese (Japan)' },
		{ code: 'ko-KR', label: 'Korean (Korea)' },
		{ code: 'pl-PL', label: 'Polish (Poland)' },
		{ code: 'pt-BR', label: 'Portuguese (Brazil)' },
		{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
		{ code: 'ro-RO', label: 'Romanian (Romania)' },
		{ code: 'ru-RU', label: 'Russian (Russia)' },
		{ code: 'es-ES', label: 'Spanish (Spain)' },
		{ code: 'es-US', label: 'Spanish (US)' },
		{ code: 'sv-SE', label: 'Swedish (Sweden)' },
		{ code: 'tr-TR', label: 'Turkish (Turkey)' },
		{ code: 'uk-UA', label: 'Ukrainian (Ukraine)' },
		{ code: 'vi-VN', label: 'Vietnamese (Vietnam)' },
	],

	// speechmatics: Speechmatics' real-time API, reached through
	// speechmatics_client.py, whose _normalize_lang reduces a BCP-47 code to
	// its bare ISO 639-1 form before sending ("en-US" -> "en") because the
	// real-time API rejects a region-qualified code outright. The three
	// English variants are therefore not separate recognition languages --
	// one accent-agnostic English pack already covers UK/US/AU/NZ
	// recognition -- they exist so the operator can pick a spelling
	// convention, which _stt_process_config forwards as --output-locale.
	// "en-US" is listed first because it is JobConfig.cc1_lang's own
	// default, so a settings load can never silently rewrite it.
	//
	// Only the English set is listed here: Speechmatics gates its remaining
	// real-time languages on the account's own contract, so the rest belong
	// in this list only once they are read back from the vendor's Feature
	// Discovery endpoint -- not copied from the published table, which mixes
	// in batch-only and bilingual/composite codes this picker does not model.
	speechmatics: [
		{ code: 'en-US', label: 'English (US)' },
		{ code: 'en-GB', label: 'English (UK)' },
		{ code: 'en-AU', label: 'English (Australia)' },
		{ code: 'en', label: 'English' },
	],
};

export const CC3_LANGUAGES = [
	{ code: 'ab', label: 'Abkhaz' },
	{ code: 'ace', label: 'Acehnese' },
	{ code: 'ach', label: 'Acholi' },
	{ code: 'af', label: 'Afrikaans' },
	{ code: 'sq', label: 'Albanian' },
	{ code: 'alz', label: 'Alur' },
	{ code: 'am', label: 'Amharic' },
	{ code: 'ar', label: 'Arabic' },
	{ code: 'hy', label: 'Armenian' },
	{ code: 'as', label: 'Assamese' },
	{ code: 'awa', label: 'Awadhi' },
	{ code: 'ay', label: 'Aymara' },
	{ code: 'az', label: 'Azerbaijani' },
	{ code: 'ban', label: 'Balinese' },
	{ code: 'bm', label: 'Bambara' },
	{ code: 'ba', label: 'Bashkir' },
	{ code: 'eu', label: 'Basque' },
	{ code: 'btx', label: 'Batak Karo' },
	{ code: 'bts', label: 'Batak Simalungun' },
	{ code: 'bbc', label: 'Batak Toba' },
	{ code: 'be', label: 'Belarusian' },
	{ code: 'bem', label: 'Bemba' },
	{ code: 'bn', label: 'Bengali' },
	{ code: 'bew', label: 'Betawi' },
	{ code: 'bho', label: 'Bhojpuri' },
	{ code: 'bik', label: 'Bikol' },
	{ code: 'bs', label: 'Bosnian' },
	{ code: 'br', label: 'Breton' },
	{ code: 'bg', label: 'Bulgarian' },
	{ code: 'bua', label: 'Buryat' },
	{ code: 'yue', label: 'Cantonese' },
	{ code: 'ca', label: 'Catalan' },
	{ code: 'ceb', label: 'Cebuano' },
	{ code: 'ny', label: 'Chichewa' },
	{ code: 'zh', label: 'Chinese (Simplified)' },
	{ code: 'zh-TW', label: 'Chinese (Traditional)' },
	{ code: 'cv', label: 'Chuvash' },
	{ code: 'co', label: 'Corsican' },
	{ code: 'crh', label: 'Crimean Tatar' },
	{ code: 'hr', label: 'Croatian' },
	{ code: 'cs', label: 'Czech' },
	{ code: 'da', label: 'Danish' },
	{ code: 'dv', label: 'Dhivehi' },
	{ code: 'din', label: 'Dinka' },
	{ code: 'doi', label: 'Dogri' },
	{ code: 'dov', label: 'Dombe' },
	{ code: 'nl', label: 'Dutch' },
	{ code: 'dz', label: 'Dzongkha' },
	{ code: 'en', label: 'English' },
	{ code: 'eo', label: 'Esperanto' },
	{ code: 'et', label: 'Estonian' },
	{ code: 'ee', label: 'Ewe' },
	{ code: 'fj', label: 'Fijian' },
	{ code: 'tl', label: 'Filipino' },
	{ code: 'fi', label: 'Finnish' },
	{ code: 'fr', label: 'French' },
	{ code: 'fr-CA', label: 'French (Canada)' },
	{ code: 'fy', label: 'Frisian' },
	{ code: 'ff', label: 'Fulani' },
	{ code: 'gaa', label: 'Ga' },
	{ code: 'gl', label: 'Galician' },
	{ code: 'ka', label: 'Georgian' },
	{ code: 'de', label: 'German' },
	{ code: 'el', label: 'Greek' },
	{ code: 'gn', label: 'Guarani' },
	{ code: 'gu', label: 'Gujarati' },
	{ code: 'ht', label: 'Haitian Creole' },
	{ code: 'cnh', label: 'Hakha Chin' },
	{ code: 'ha', label: 'Hausa' },
	{ code: 'haw', label: 'Hawaiian' },
	{ code: 'he', label: 'Hebrew' },
	{ code: 'hil', label: 'Hiligaynon' },
	{ code: 'hi', label: 'Hindi' },
	{ code: 'hmn', label: 'Hmong' },
	{ code: 'hu', label: 'Hungarian' },
	{ code: 'hrx', label: 'Hunsrik' },
	{ code: 'is', label: 'Icelandic' },
	{ code: 'ig', label: 'Igbo' },
	{ code: 'ilo', label: 'Ilocano' },
	{ code: 'id', label: 'Indonesian' },
	{ code: 'ga', label: 'Irish' },
	{ code: 'it', label: 'Italian' },
	{ code: 'ja', label: 'Japanese' },
	{ code: 'jv', label: 'Javanese' },
	{ code: 'kn', label: 'Kannada' },
	{ code: 'pam', label: 'Kapampangan' },
	{ code: 'kk', label: 'Kazakh' },
	{ code: 'km', label: 'Khmer' },
	{ code: 'cgg', label: 'Kiga' },
	{ code: 'rw', label: 'Kinyarwanda' },
	{ code: 'ktu', label: 'Kituba' },
	{ code: 'gom', label: 'Konkani' },
	{ code: 'ko', label: 'Korean' },
	{ code: 'kri', label: 'Krio' },
	{ code: 'ku', label: 'Kurdish (Kurmanji)' },
	{ code: 'ckb', label: 'Kurdish (Sorani)' },
	{ code: 'ky', label: 'Kyrgyz' },
	{ code: 'lo', label: 'Lao' },
	{ code: 'ltg', label: 'Latgalian' },
	{ code: 'la', label: 'Latin' },
	{ code: 'lv', label: 'Latvian' },
	{ code: 'lij', label: 'Ligurian' },
	{ code: 'li', label: 'Limburgish' },
	{ code: 'ln', label: 'Lingala' },
	{ code: 'lt', label: 'Lithuanian' },
	{ code: 'lmo', label: 'Lombard' },
	{ code: 'lg', label: 'Luganda' },
	{ code: 'luo', label: 'Luo' },
	{ code: 'lb', label: 'Luxembourgish' },
	{ code: 'mk', label: 'Macedonian' },
	{ code: 'mai', label: 'Maithili' },
	{ code: 'mak', label: 'Makassar' },
	{ code: 'mg', label: 'Malagasy' },
	{ code: 'ms', label: 'Malay' },
	{ code: 'ms-Arab', label: 'Malay (Jawi)' },
	{ code: 'ml', label: 'Malayalam' },
	{ code: 'mt', label: 'Maltese' },
	{ code: 'mi', label: 'Maori' },
	{ code: 'mr', label: 'Marathi' },
	{ code: 'chm', label: 'Meadow Mari' },
	{ code: 'mni-Mtei', label: 'Meiteilon (Manipuri)' },
	{ code: 'min', label: 'Minang' },
	{ code: 'lus', label: 'Mizo' },
	{ code: 'mn', label: 'Mongolian' },
	{ code: 'my', label: 'Myanmar (Burmese)' },
	{ code: 'nr', label: 'Ndebele (South)' },
	{ code: 'new', label: 'Nepalbhasa (Newari)' },
	{ code: 'ne', label: 'Nepali' },
	{ code: 'no', label: 'Norwegian' },
	{ code: 'nus', label: 'Nuer' },
	{ code: 'oc', label: 'Occitan' },
	{ code: 'or', label: 'Odia (Oriya)' },
	{ code: 'om', label: 'Oromo' },
	{ code: 'pag', label: 'Pangasinan' },
	{ code: 'pap', label: 'Papiamento' },
	{ code: 'ps', label: 'Pashto' },
	{ code: 'fa', label: 'Persian' },
	{ code: 'pl', label: 'Polish' },
	{ code: 'pt', label: 'Portuguese (Brazil)' },
	{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
	{ code: 'pa', label: 'Punjabi (Gurmukhi)' },
	{ code: 'pa-Arab', label: 'Punjabi (Shahmukhi)' },
	{ code: 'qu', label: 'Quechua' },
	{ code: 'rom', label: 'Romani' },
	{ code: 'ro', label: 'Romanian' },
	{ code: 'rn', label: 'Rundi' },
	{ code: 'ru', label: 'Russian' },
	{ code: 'sm', label: 'Samoan' },
	{ code: 'sg', label: 'Sango' },
	{ code: 'sa', label: 'Sanskrit' },
	{ code: 'gd', label: 'Scots Gaelic' },
	{ code: 'nso', label: 'Sepedi' },
	{ code: 'sr', label: 'Serbian' },
	{ code: 'st', label: 'Sesotho' },
	{ code: 'crs', label: 'Seychellois Creole' },
	{ code: 'shn', label: 'Shan' },
	{ code: 'sn', label: 'Shona' },
	{ code: 'scn', label: 'Sicilian' },
	{ code: 'szl', label: 'Silesian' },
	{ code: 'sd', label: 'Sindhi' },
	{ code: 'si', label: 'Sinhala' },
	{ code: 'sk', label: 'Slovak' },
	{ code: 'sl', label: 'Slovenian' },
	{ code: 'so', label: 'Somali' },
	{ code: 'es', label: 'Spanish' },
	{ code: 'su', label: 'Sundanese' },
	{ code: 'sw', label: 'Swahili' },
	{ code: 'ss', label: 'Swati' },
	{ code: 'sv', label: 'Swedish' },
	{ code: 'tg', label: 'Tajik' },
	{ code: 'ta', label: 'Tamil' },
	{ code: 'tt', label: 'Tatar' },
	{ code: 'te', label: 'Telugu' },
	{ code: 'tet', label: 'Tetum' },
	{ code: 'th', label: 'Thai' },
	{ code: 'ti', label: 'Tigrinya' },
	{ code: 'ts', label: 'Tsonga' },
	{ code: 'tn', label: 'Tswana' },
	{ code: 'tr', label: 'Turkish' },
	{ code: 'tk', label: 'Turkmen' },
	{ code: 'ak', label: 'Twi' },
	{ code: 'uk', label: 'Ukrainian' },
	{ code: 'ur', label: 'Urdu' },
	{ code: 'ug', label: 'Uyghur' },
	{ code: 'uz', label: 'Uzbek' },
	{ code: 'vi', label: 'Vietnamese' },
	{ code: 'cy', label: 'Welsh' },
	{ code: 'xh', label: 'Xhosa' },
	{ code: 'yi', label: 'Yiddish' },
	{ code: 'yo', label: 'Yoruba' },
	{ code: 'yua', label: 'Yucatec Maya' },
	{ code: 'zu', label: 'Zulu' },
];
